import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScienceIcon from '@mui/icons-material/Science';
import CloseIcon from '@mui/icons-material/Close';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
  buildSlideNumberMap,
  createPreviewBlobUrl,
  extractSectionInnerHTML,
  patchSectionContent,
} from '../../utils/slidePreview';
import { SlideVerificationDialog } from '../../components/common/SlideVerificationDialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { imageService } from '../../services/imageService';
import { lessonService } from '../../services/lessonService';
import { slidesService } from '../../services/slidesService';
import { useSSEValidation } from '../../hooks/useSSEValidation';
import { useLessonStore } from '../../store/lessonStore';
import { useEditorStore } from '../../store/editorStore';
import type { ChatMessage, Image, SlideData, SlidesHTML } from '../../types';

interface Phase3Props {
  lessonId: string;
  readOnly?: boolean;
}

/** Read-only MARP deck iframe that injects generated images and navigates to a specific slide. */
const SlidePreviewIframe: React.FC<{
  fullMarpHTML: string;
  images: Image[];
  startIndex: number;
}> = ({ fullMarpHTML, images, startIndex }) => {
  const [blobUrl, setBlobUrl] = useState('');

  useEffect(() => {
    if (!fullMarpHTML) return;
    const { url, revoke } = createPreviewBlobUrl(fullMarpHTML, images, startIndex);
    setBlobUrl(url);
    return () => { revoke(); setBlobUrl(''); };
  }, [fullMarpHTML, images, startIndex]);

  return (
    <Box sx={{ width: '100%', aspectRatio: '16/9', bgcolor: '#1a1a1a' }}>
      <iframe
        src={blobUrl || undefined}
        title="Slide Preview"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
    </Box>
  );
};

const STATUS_ACCENT: Record<Image['status'], string> = {
  pending:    '#bdbdbd',
  generating: '#fb8c00',
  generated:  '#43a047',
  uploaded:   '#1e88e5',
  failed:     '#e53935',
};

const STATUS_LABEL: Record<Image['status'], string> = {
  pending:    'Pending',
  generating: 'Generating…',
  generated:  'Generated',
  uploaded:   'Uploaded',
  failed:     'Failed',
};

const ImageCard: React.FC<{
  image: Image;
  slideLabel: string;
  resolvedSlideNumber: number;
  onGenerate: (id: string) => void;
  onRegenerate: (id: string) => void;
  onUpload: (id: string, file: File) => void;
  onPromptChange: (id: string, prompt: string) => void;
  onPreviewInSlide: (slideIndex: number) => void;
  onImageClick: (url: string) => void;
  isGenerating: boolean;
  readOnly: boolean;
  hasSlidePreview: boolean;
}> = ({
  image, slideLabel, resolvedSlideNumber,
  onGenerate, onRegenerate, onUpload, onPromptChange,
  onPreviewInSlide, onImageClick,
  isGenerating, readOnly, hasSlidePreview,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localPrompt, setLocalPrompt] = useState(image.prompt);
  const isComplete = image.status === 'generated' || image.status === 'uploaded';
  const accentColor = STATUS_ACCENT[image.status];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(image.placeholder_id, file);
    e.target.value = '';
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'box-shadow 0.22s ease, transform 0.22s ease',
        '&:hover': {
          boxShadow: '0 8px 28px rgba(0,0,0,0.13)',
          transform: 'translateY(-3px)',
        },
      }}
    >
      {/* Status accent strip */}
      <Box sx={{ height: 4, bgcolor: accentColor, transition: 'background-color 0.3s' }} />

      {/* Image area */}
      <Box
        onClick={() => { if (image.blob_url && !isGenerating) onImageClick(image.blob_url); }}
        sx={{
          position: 'relative',
          height: 210,
          bgcolor: '#f3f4f6',
          cursor: image.blob_url && !isGenerating ? 'zoom-in' : 'default',
          overflow: 'hidden',
          '&:hover .img-overlay': { opacity: image.blob_url && !isGenerating ? 1 : 0 },
        }}
      >
        {isGenerating ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5 }}>
            <CircularProgress size={36} thickness={3.5} sx={{ color: accentColor }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.4, fontWeight: 500 }}>
              Generating image…
            </Typography>
          </Box>
        ) : image.blob_url ? (
          <Box
            component="img"
            src={image.blob_url}
            alt={image.prompt}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#e9eaed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ImageIcon sx={{ fontSize: 32, color: '#9e9e9e' }} />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 500 }}>No image yet</Typography>
          </Box>
        )}

        {/* Zoom overlay on hover */}
        <Box
          className="img-overlay"
          sx={{
            position: 'absolute', inset: 0,
            bgcolor: 'rgba(0,0,0,0.32)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 0.2s ease',
          }}
        >
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '50%', p: 1.5, backdropFilter: 'blur(4px)' }}>
            <ZoomInIcon sx={{ color: 'white', fontSize: 28, display: 'block' }} />
          </Box>
        </Box>

        {/* Bottom gradient: slide label + status badge */}
        <Box
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)',
            px: 1.5, py: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.6, fontSize: '0.7rem', textTransform: 'uppercase' }}>
            {slideLabel}
          </Typography>
          <Chip
            label={STATUS_LABEL[image.status]}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.62rem',
              fontWeight: 700,
              bgcolor: accentColor,
              color: 'white',
              borderRadius: '4px',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
      </Box>

      {/* Prompt */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <TextField
          size="small"
          label="Image Prompt"
          multiline
          rows={2}
          fullWidth
          value={localPrompt}
          onChange={(e) => setLocalPrompt(e.target.value)}
          onBlur={() => { if (localPrompt !== image.prompt) onPromptChange(image.placeholder_id, localPrompt); }}
          disabled={readOnly || isGenerating}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' },
            '& .MuiInputLabel-root': { fontSize: '0.8rem' },
          }}
        />
      </Box>

      {/* Actions */}
      <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Primary action: Generate / Regenerate */}
        {!isComplete ? (
          <Button
            size="small"
            variant="contained"
            startIcon={isGenerating ? <CircularProgress size={13} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={() => onGenerate(image.placeholder_id)}
            disabled={isGenerating || readOnly}
            disableElevation
            sx={{ borderRadius: 2, flex: 1, fontSize: '0.78rem', fontWeight: 600 }}
          >
            Generate
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => onRegenerate(image.placeholder_id)}
            disabled={isGenerating || readOnly}
            sx={{ borderRadius: 2, flex: 1, fontSize: '0.78rem', fontWeight: 600 }}
          >
            Regenerate
          </Button>
        )}

        {/* Upload */}
        <Tooltip title="Upload a custom image">
          <span>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={isGenerating || readOnly}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: '6px' }}
            >
              <UploadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* In Slide preview */}
        {hasSlidePreview && resolvedSlideNumber > 0 && (
          <Tooltip title={`Preview in Slide ${resolvedSlideNumber}`}>
            <span>
              <IconButton
                size="small"
                onClick={() => onPreviewInSlide(resolvedSlideNumber - 1)}
                disabled={isGenerating}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: '6px' }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Complete badge */}
        {isComplete && (
          <Tooltip title="Image ready">
            <CheckCircleIcon sx={{ color: accentColor, fontSize: 22, ml: 'auto' }} />
          </Tooltip>
        )}
      </Box>

      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
    </Card>
  );
};

export const Phase3Images: React.FC<Phase3Props> = ({ lessonId, readOnly = false }) => {
  const { currentVersion, updateSlidesHTML } = useLessonStore();
  const { setPhase, markDirty } = useEditorStore();
  const { refineStage: refineStageSSE } = useSSEValidation();
  const queryClient = useQueryClient();
  const versionNumber = currentVersion?.version_number ?? 1;

  const fullMarpHTML =
    ((currentVersion?.slides_html as Record<string, unknown>)?.['__marp_full_html__'] as string) ?? '';

  const [promptOverrides, setPromptOverrides] = useState<Record<string, string>>({});
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [proceedError, setProceedError] = useState('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewStartIndex, setPreviewStartIndex] = useState(0);

  const { data: imageData, isLoading, isError } = useQuery({
    queryKey: ['images', lessonId, versionNumber],
    queryFn: () => imageService.getImages(lessonId, versionNumber),
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      const imgs = query.state.data?.images ?? [];
      const hasGenerating = imgs.some((img) => img.status === 'generating');
      return hasGenerating ? 5000 : false;
    },
  });

  const images = imageData?.images ?? [];
  const realGeneration = imageData?.realGeneration ?? false;
  const provider = imageData?.provider ?? 'placeholder';
  const imageModel = imageData?.imageModel ?? '';

  const hasAnyImage = images.some((img) => img.blob_url);

  // Derive slide numbers from the MARP HTML so labels are correct even for
  // records seeded before slide_number was stored in the database.
  const slideNumberMap = useMemo(() => buildSlideNumberMap(fullMarpHTML), [fullMarpHTML]);
  const resolvedSlideNumber = (img: Image) =>
    slideNumberMap.get(img.placeholder_id) ?? img.slide_number;
  const slideLabel = (img: Image) => {
    const n = resolvedSlideNumber(img);
    return n > 0 ? `Slide ${n}` : img.placeholder_id;
  };

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const openDeckPreview = () => { setPreviewStartIndex(0); setPreviewOpen(true); };
  const openSlidePreview = (slideIndex: number) => { setPreviewStartIndex(slideIndex); setPreviewOpen(true); };

  const chip = {
    nanobana:    { label: 'Nano Banana',               tooltip: 'Nano Banana AI generation is active',                                                         color: 'primary'   as const },
    openrouter:  { label: `OpenRouter${imageModel ? ` · ${imageModel.split('/').pop()}` : ''}`, tooltip: `OpenRouter · ${imageModel || 'image model'} is active`, color: 'secondary' as const },
    placeholder: { label: 'Placeholder',               tooltip: 'Placeholder mode — set IMAGE_PROVIDER=nanobana or openrouter to enable real generation',       color: 'default'   as const },
  }[provider] ?? { label: 'Placeholder', tooltip: 'Placeholder mode', color: 'default' as const };

  const completedCount = images.filter(
    (img) => img.status === 'generated' || img.status === 'uploaded'
  ).length;
  const totalCount = images.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allComplete = totalCount > 0 && completedCount === totalCount;

  const imagesQueryKey = ['images', lessonId, versionNumber] as const;

  const generateMutation = useMutation({
    mutationFn: ({ placeholderId, prompt }: { placeholderId: string; prompt?: string }) =>
      imageService.generateImage(lessonId, versionNumber, placeholderId, prompt),
    onMutate: ({ placeholderId }) => {
      setGeneratingIds((prev) => new Set([...prev, placeholderId]));
      // Optimistically clear the stale blob_url so the spinner shows immediately
      // instead of the old image persisting during the round-trip.
      queryClient.setQueryData(imagesQueryKey, (old: import('../../services/imageService').ImageListResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          images: old.images.map((img) =>
            img.placeholder_id === placeholderId
              ? { ...img, status: 'generating' as const, blob_url: undefined }
              : img
          ),
        };
      });
    },
    onSettled: (_data, _err, { placeholderId }) => {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(placeholderId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: imagesQueryKey });
    },
  });

  const generateAllMutation = useMutation({
    mutationFn: () => imageService.generateAllImages(lessonId, versionNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images', lessonId, versionNumber] });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ placeholderId, file }: { placeholderId: string; file: File }) =>
      imageService.uploadImage(lessonId, versionNumber, placeholderId, file),
    onMutate: ({ placeholderId }) => {
      setGeneratingIds((prev) => new Set([...prev, placeholderId]));
    },
    onSettled: (_data, _err, { placeholderId }) => {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(placeholderId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['images', lessonId, versionNumber] });
    },
  });

  const handlePromptChange = (placeholderId: string, prompt: string) => {
    setPromptOverrides((prev) => ({ ...prev, [placeholderId]: prompt }));
  };

  // Maps a MARP section index to the owning stage name + local slide index.
  // Returns null for intro/divider sections that don't belong to a content stage.
  const resolveMarpIndex = useCallback((marpIndex: number, html: string) => {
    const stages = currentVersion?.lesson_plan?.stages ?? [];
    const slideMapping = (currentVersion?.slide_mapping ?? {}) as Record<string, number>;
    const totalSections = (html.match(/<section[^>]*>/g) ?? []).length;
    const totalContent = stages.reduce((s, st) => s + (slideMapping[st.name] ?? 2), 0);
    const introCount = Math.max(0, totalSections - stages.length - totalContent);

    let secIdx = introCount;
    for (const stage of stages) {
      secIdx++; // divider
      const count = slideMapping[stage.name] ?? 2;
      for (let i = 0; i < count; i++) {
        if (secIdx === marpIndex) return { stageName: stage.name, stageLocalIndex: i };
        secIdx++;
      }
    }
    return null;
  }, [currentVersion]);

  // Snapshot taken before each preview generation — used to restore on discard.
  const preFixSnapshotRef = useRef<Record<string, unknown> | null>(null);
  // Latest per-stage slides_html read from DB after a refineStageSSE call.
  const freshPerStageRef = useRef<Record<string, unknown> | null>(null);

  /**
   * Generate a proposed fix via the LLM and return the new full MARP HTML for
   * preview. Does NOT save __marp_full_html__ to the DB or store — the author
   * must explicitly click Apply.
   */
  const handleGeneratePreview = useCallback(async (
    marpIndex: number,
    message: string,
    chatHistory: ChatMessage[],
    currentFullMarpHTML: string,
  ): Promise<string> => {
    const resolved = resolveMarpIndex(marpIndex, currentFullMarpHTML);
    if (!resolved) throw new Error('This slide is not a content slide');
    const { stageName, stageLocalIndex } = resolved;

    // Snapshot the current state so Discard can restore it.
    preFixSnapshotRef.current = currentVersion?.slides_html as Record<string, unknown>;

    const slidesHTML = currentVersion?.slides_html ?? {};
    const stageSlides = (slidesHTML[stageName] ?? []) as SlideData[];
    const liveHTML = extractSectionInnerHTML(currentFullMarpHTML, marpIndex);
    const enrichedSlides = stageSlides.map((s, i) =>
      i === stageLocalIndex ? { ...s, html: liveHTML } : s,
    );

    await refineStageSSE({
      lesson_id: lessonId,
      version: versionNumber,
      stage_name: stageName,
      message: `Slide ${stageLocalIndex + 1} ONLY: ${message}`,
      current_slides: enrichedSlides,
      history: chatHistory,
    });

    const freshData = await lessonService.getById(lessonId);
    if (!freshData?.version?.slides_html) throw new Error('Failed to read updated slides');

    // Keep the fresh per-stage data so Apply can use it without another DB round-trip.
    freshPerStageRef.current = freshData.version.slides_html as Record<string, unknown>;

    const refinedSlide = ((freshData.version.slides_html as SlidesHTML)[stageName] ?? [])
      .find((s: SlideData) => s.slide_number === stageLocalIndex + 1);
    if (!refinedSlide?.html) throw new Error('No updated HTML returned');

    // Return preview HTML only — __marp_full_html__ is intentionally NOT saved here.
    return patchSectionContent(currentFullMarpHTML, marpIndex, refinedSlide.html);
  }, [lessonId, versionNumber, currentVersion, resolveMarpIndex, refineStageSSE]);

  /** Commit the pending fix: save __marp_full_html__ to DB and update the store. */
  const handleApplyFix = useCallback(async (
    _marpIndex: number,
    previewHTML: string,
  ): Promise<void> => {
    const perStage = freshPerStageRef.current ?? (currentVersion?.slides_html as Record<string, unknown>);
    const merged = { ...perStage, __marp_full_html__: previewHTML };
    await lessonService.saveVersion(lessonId, versionNumber, { slides_html: merged });
    updateSlidesHTML(merged as unknown as SlidesHTML);
    markDirty();
    preFixSnapshotRef.current = null;
    freshPerStageRef.current = null;
  }, [lessonId, versionNumber, currentVersion, updateSlidesHTML, markDirty]);

  /** Discard: restore the per-stage slides to their pre-fix state in DB and store. */
  const handleDiscardFix = useCallback(async (_marpIndex: number): Promise<void> => {
    const snapshot = preFixSnapshotRef.current;
    if (!snapshot) return;
    await lessonService.saveVersion(lessonId, versionNumber, { slides_html: snapshot });
    updateSlidesHTML(snapshot as unknown as SlidesHTML);
    preFixSnapshotRef.current = null;
    freshPerStageRef.current = null;
  }, [lessonId, versionNumber, updateSlidesHTML]);

  const handleProceed = async () => {
    setProceedError('');
    setVerifyOpen(false);
    try {
      await slidesService.assembleForPhase4(lessonId, versionNumber);
      setPhase(4);
    } catch {
      setProceedError('Failed to assemble slides. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load images. Please refresh.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Progress */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              Image Management
            </Typography>
            <Tooltip title={chip.tooltip}>
              <Chip
                size="small"
                icon={realGeneration ? <AutoAwesomeIcon /> : <ScienceIcon />}
                label={chip.label}
                color={chip.color}
                variant={realGeneration ? 'filled' : 'outlined'}
              />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {completedCount} / {totalCount} images completed
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          color={allComplete ? 'success' : 'primary'}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Tooltip title={hasAnyImage ? 'Preview slides with generated images' : 'Generate at least one image to preview'}>
          <span>
            <Button
              variant="outlined"
              startIcon={<SlideshowIcon />}
              onClick={openDeckPreview}
              disabled={!hasAnyImage || !fullMarpHTML}
            >
              Preview Deck
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={
            generateAllMutation.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <AutoAwesomeIcon />
            )
          }
          onClick={() => generateAllMutation.mutate()}
          disabled={generateAllMutation.isPending || allComplete || readOnly || totalCount === 0}
        >
          Generate All Remaining
        </Button>
      </Box>

      {generateAllMutation.isError && (
        <Alert severity="error">Failed to generate images. Please try again.</Alert>
      )}

      {totalCount === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
          <ImageIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6">No image placeholders</Typography>
          <Typography variant="body2">
            The generated slides don't contain image placeholders.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {images.map((image) => {
            const effectiveImage: Image = promptOverrides[image.placeholder_id]
              ? { ...image, prompt: promptOverrides[image.placeholder_id] }
              : image;
            return (
              <ImageCard
                key={image.id}
                image={effectiveImage}
                slideLabel={slideLabel(effectiveImage)}
                resolvedSlideNumber={resolvedSlideNumber(effectiveImage)}
                onGenerate={(id) => generateMutation.mutate({ placeholderId: id, prompt: promptOverrides[id] })}
                onRegenerate={(id) => generateMutation.mutate({ placeholderId: id, prompt: promptOverrides[id] })}
                onUpload={(id, file) => uploadMutation.mutate({ placeholderId: id, file })}
                onPromptChange={handlePromptChange}
                onPreviewInSlide={openSlidePreview}
                onImageClick={setLightboxUrl}
                isGenerating={
                  generatingIds.has(image.placeholder_id) ||
                  image.status === 'generating'
                }
                readOnly={readOnly}
                hasSlidePreview={!!fullMarpHTML}
              />
            );
          })}
        </Box>
      )}

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        {proceedError && <Alert severity="error">{proceedError}</Alert>}
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => setVerifyOpen(true)}
          disabled={readOnly || !allComplete}
          sx={{ borderRadius: 2, px: 4 }}
        >
          Proceed to Phase 4
        </Button>
      </Box>

      {/* Image Lightbox — full-screen view of a single generated image */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'transparent',
              boxShadow: 'none',
              m: 1,
              maxWidth: '95vw',
              maxHeight: '95vh',
            },
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxUrl(null)}
            size="small"
            sx={{
              position: 'absolute', top: 8, right: 8, zIndex: 1,
              bgcolor: 'rgba(0,0,0,0.55)', color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {lightboxUrl && (
            <Box
              component="img"
              src={lightboxUrl}
              alt="Full size preview"
              sx={{
                display: 'block',
                maxWidth: '95vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* Slide Preview Dialog — Option A (per-card) + Option C (full deck) */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        slotProps={{ paper: { sx: { bgcolor: '#1a1a1a' } } }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SlideshowIcon fontSize="small" />
            {previewStartIndex === 0
              ? 'Deck Preview'
              : `Slide ${previewStartIndex + 1} Preview`}
          </Box>
          <IconButton size="small" onClick={() => setPreviewOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewOpen && fullMarpHTML && (
            <SlidePreviewIframe
              fullMarpHTML={fullMarpHTML}
              images={images}
              startIndex={previewStartIndex}
            />
          )}
        </DialogContent>
      </Dialog>

      <SlideVerificationDialog
        open={verifyOpen}
        fullMarpHTML={fullMarpHTML}
        images={images}
        onGeneratePreview={handleGeneratePreview}
        onApplyFix={handleApplyFix}
        onDiscardFix={handleDiscardFix}
        onProceed={() => void handleProceed()}
        onClose={() => setVerifyOpen(false)}
      />
    </Box>
  );
};
